/**
 * The API client.
 *
 * Served from the same origin as the SPA (docs/11-deployment.md), so every path here is
 * relative: no base URL, no CORS, nothing to configure per environment — and one less thing
 * that can be wrong in a deploy.
 *
 * Errors arrive as `{ error: { code, message, details?, rule_id? } }`. `rule_id` cites the
 * policy rule behind a refusal, so a screen can say *"below the 50% participation minimum
 * (R-13)"* instead of "validation failed", which is the difference between a manager who
 * knows what to do next and one who emails HR.
 */
import type {
  AdminUser,
  AttendeesResult,
  BudgetYear,
  Calculation,
  Claim,
  ClaimListItem,
  ClaimStatus,
  ConfirmPreview,
  DepartmentPosition,
  AssignmentList,
  DepartmentSummary,
  EntitlementTier,
  HrDirectors,
  FeedbackCategory,
  FeedbackContext,
  FeedbackStatus,
  JobList,
  JobRunResult,
  ManagedFeedbackList,
  MyFeedbackList,
  NotificationList,
  OutboxList,
  OutboxStatus,
  ReportData,
  Setting,
  LedgerEntry,
  Me,
  Meta,
  PendingUser,
  Proposal,
  ProposalListItem,
  ProposalStatus,
  Readiness,
  RosterEmployee,
  RosterFreshness,
  Settlement,
  SponsorCoverage,
  SubmitResult,
  SyncRun,
} from './types';

import { sessionToken } from './clerk';

export interface ApiErrorBody {
  error: { code: string; message: string; details?: unknown; rule_id?: string };
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly ruleId?: string | undefined,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** Layer 2 held, Layer 1 did not — show the in-app no-access state, never sign out. */
  get isAccessRefusal(): boolean {
    return this.code === 'not_registered' || this.code === 'deactivated';
  }

  get isUnauthenticated(): boolean {
    return this.status === 401;
  }

  /** Someone else moved the record since this screen loaded. Reload, do not overwrite. */
  get isStale(): boolean {
    return this.code === 'stale_proposal' || this.code === 'stale_claim';
  }
}

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH';

/** Which verifier the API reported at `/meta`. Null until that call has answered. */
let verifierMode: Meta['auth'] | null = null;

async function request<T>(
  method: Method,
  path: string,
  body?: unknown,
  headers: Record<string, string> = {},
): Promise<T> {
  // A fresh token per call. Clerk's session tokens are short-lived by design and `getToken()`
  // refreshes when it needs to, so caching one here would only be a way to send a stale one.
  // Skipped entirely when this build is not verifying with Clerk, or when somebody has chosen a
  // dev identity — the two mechanisms are mutually exclusive, and asking Clerk anyway would mean
  // fetching 500 kB of SDK to be told there is no session.
  const token = verifierMode === 'dev-stub' || devIdentity() !== null ? null : await sessionToken();

  const response = await fetch(path, {
    method,
    headers: {
      accept: 'application/json',
      ...(body === undefined ? {} : { 'content-type': 'application/json' }),
      ...(token === null ? {} : { authorization: `Bearer ${token}` }),
      ...devHeaders(),
      ...headers,
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });

  const text = await response.text();
  let parsed: unknown;
  try {
    parsed = text ? JSON.parse(text) : null;
  } catch {
    // A non-JSON body from an API path usually means index.html came back — i.e. the route
    // does not exist. Say that rather than "Unexpected token <".
    throw new ApiError(response.status, 'not_json', `${path} did not return JSON`);
  }

  if (!response.ok) {
    const problem = (parsed as ApiErrorBody | null)?.error;
    throw new ApiError(
      response.status,
      problem?.code ?? 'request_failed',
      problem?.message ?? `${path} failed with ${response.status}`,
      problem?.rule_id,
      problem?.details,
    );
  }
  return parsed as T;
}

/**
 * The dev identity, until Clerk lands at M9.
 *
 * The API's fenced stub reads `X-Dev-Email` (`auth/dev-identity.ts`), and it only exists in a
 * build where `AUTH_DEV_BYPASS` is on and `APP_ENV=dev`. Sending the header at other times is
 * harmless — production has no verifier that reads it — but it is only *set* when someone has
 * deliberately chosen an identity, so the normal path sends nothing.
 */
function devHeaders(): Record<string, string> {
  const email = devIdentity();
  return email ? { 'x-dev-email': email } : {};
}

const DEV_IDENTITY_KEY = 'dept-bonding.dev-email';

export function devIdentity(): string | null {
  try {
    return globalThis.localStorage?.getItem(DEV_IDENTITY_KEY) ?? null;
  } catch {
    return null;
  }
}

export function setDevIdentity(email: string | null): void {
  try {
    if (email) globalThis.localStorage?.setItem(DEV_IDENTITY_KEY, email);
    else globalThis.localStorage?.removeItem(DEV_IDENTITY_KEY);
  } catch {
    /* Private browsing, or storage disabled. The app still works, just not as someone else. */
  }
}

const get = <T>(path: string): Promise<T> => request<T>('GET', path);
const post = <T>(path: string, body?: unknown, headers?: Record<string, string>): Promise<T> =>
  request<T>('POST', path, body ?? {}, headers);
const put = <T>(path: string, body: unknown): Promise<T> => request<T>('PUT', path, body);
const patch = <T>(path: string, body: unknown): Promise<T> => request<T>('PATCH', path, body);

const query = (
  params: Record<string, string | number | boolean | undefined | readonly string[]>,
): string => {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined) continue;
    if (Array.isArray(value)) for (const item of value) search.append(key, item);
    else search.append(key, String(value));
  }
  const rendered = search.toString();
  return rendered ? `?${rendered}` : '';
};

/**
 * A fresh key per user action, so a double-tapped Approve on a phone settles once.
 *
 * Generated in the browser and held for the life of one attempt: a retry of the *same* attempt
 * reuses it and replays the original answer, while a deliberate second action gets a new one.
 */
export const idempotencyKey = (): string =>
  globalThis.crypto?.randomUUID?.() ?? `key-${Date.now()}-${Math.random().toString(36).slice(2)}`;

// ------------------------------------------------------------------------ session ---------

export const api = {
  meta: async (): Promise<Meta> => {
    const meta = await get<Meta>('/api/v1/meta');
    // Remembered so the request path knows whether to ask Clerk for a token at all. `/meta` is
    // unauthenticated and is fetched before anything else, so this is always set by the time it
    // matters — and on a dev-stub build it keeps the browser from loading Clerk's SDK, or making
    // a network call to it, for a verifier that would ignore the answer.
    verifierMode = meta.auth;
    return meta;
  },

  readiness: async (): Promise<Readiness> => {
    // /readyz answers 503 when not ready, which is a real answer, not an error.
    const response = await fetch('/readyz', { headers: { accept: 'application/json' } });
    return (await response.json()) as Readiness;
  },

  me: (): Promise<Me> => get('/api/v1/me'),

  // ------------------------------------------------------------------------ budget -------

  departmentPositions: (
    year?: number,
  ): Promise<{
    budget_year: number;
    departments: DepartmentPosition[];
  }> => get(`/api/v1/budget/departments${query({ year })}`),

  calculate: (body: {
    department_id: number;
    date: string;
    employee_ids: number[];
  }): Promise<Calculation> => post('/api/v1/budget/calculate', body),

  budgetYears: (): Promise<{ years: BudgetYear[] }> => get('/api/v1/budget/years'),

  setPriorPeriodLoaded: (year: number, loaded: boolean, note?: string) =>
    post<{ year: number; prior_period_loaded_at: string | null }>(
      `/api/v1/budget/years/${year}/prior-period`,
      { loaded, ...(note === undefined ? {} : { note }) },
    ),

  budgetRates: (year?: number): Promise<{ budget_year: number; tiers: EntitlementTier[] }> =>
    get(`/api/v1/budget/rates${query({ year })}`),

  ledger: (
    employeeId: number,
    year?: number,
  ): Promise<{
    employee: { id: number; employee_no: string; full_name: string };
    budget_year: number;
    entries: LedgerEntry[];
  }> => get(`/api/v1/budget/employees/${employeeId}/ledger${query({ year })}`),

  // ------------------------------------------------------------------------ roster -------

  employees: (params: {
    department_id?: number;
    status?: string;
    category?: string;
    search?: string;
    limit?: number;
  }): Promise<{ employees: RosterEmployee[]; scope: string }> =>
    get(`/api/v1/employees${query(params)}`),

  departments: (): Promise<{ departments: DepartmentSummary[]; scope: string }> =>
    get('/api/v1/departments'),

  freshness: (): Promise<RosterFreshness> => get('/api/v1/employees/freshness'),

  syncRuns: (): Promise<{ runs: SyncRun[]; freshness: RosterFreshness }> =>
    get('/api/v1/employees/sync/runs'),

  runSync: (): Promise<Record<string, unknown>> => post('/api/v1/employees/sync'),

  sponsorCoverage: (): Promise<SponsorCoverage> => get('/api/v1/setup/sponsor-coverage'),

  hrDirectors: (): Promise<HrDirectors> => get('/api/v1/setup/hr-directors'),

  setHrDirectors: (userIds: number[]) =>
    put<{ directors: number[]; designated: number[]; ended: number[] }>(
      '/api/v1/setup/hr-directors',
      { user_ids: userIds },
    ),

  assignments: (departmentId: number, includeEnded = false): Promise<AssignmentList> =>
    get(
      `/api/v1/departments/${departmentId}/assignments${query({ include_ended: includeEnded ? 'true' : undefined })}`,
    ),

  updateAssignments: (
    departmentId: number,
    body: {
      add?: Array<{ employee_id: number; assignment: 'manager' | 'head_sponsor' }>;
      end?: number[];
    },
  ) =>
    put<{ added: number; ended: number }>(`/api/v1/departments/${departmentId}/assignments`, body),

  settings: (): Promise<{ settings: Setting[]; known_keys: string[] }> => get('/api/v1/settings'),

  setSetting: (key: string, value: string | number) =>
    put<{ key: string; value: string | number }>('/api/v1/settings', { key, value }),

  // ---------------------------------------------------------- budget administration ------

  openBudgetYear: (year: number, graceDeadline?: string) =>
    post<{ year: number; status: string }>('/api/v1/budget/years', {
      year,
      ...(graceDeadline ? { claim_grace_deadline: graceDeadline } : {}),
    }),

  closeBudgetYear: (year: number) =>
    post<{ year: number; status: string }>(`/api/v1/budget/years/${year}/close`, {}),

  setBudgetRates: (
    year: number,
    tiers: Array<{
      min_months: number;
      max_months: number | null;
      staff_cents: number;
      worker_cents: number;
    }>,
  ) =>
    put<{ budget_year: number; tiers: EntitlementTier[] }>('/api/v1/budget/rates', {
      budget_year: year,
      tiers,
    }),

  postAdjustment: (body: {
    budget_year: number;
    employee_id: number;
    amount_cents: number;
    reason: string;
    rule_id?: string;
  }) =>
    post<{
      id: number | null;
      employee_id: number;
      amount_cents: number;
      /** Stated by the API, because the sign alone reads backwards. */
      effect: 'reduces_available' | 'restores_available';
    }>('/api/v1/budget/adjustments', body),

  // ------------------------------------------------------------------------ reports ------

  reportList: (): Promise<{ reports: Array<{ name: string; path: string }> }> =>
    get('/api/v1/reports'),

  report: (name: string, params: { year?: number; department_id?: number }): Promise<ReportData> =>
    get(`/api/v1/reports/${name}${query(params)}`),

  /**
   * A URL rather than a fetch.
   *
   * The browser has to do the downloading: a `fetch` would land the spreadsheet in memory and
   * then need a synthetic anchor to get it onto disk, and the `Content-Disposition` the server
   * already sends does the same job with none of that.
   */
  reportExportUrl: (
    name: string,
    params: { year?: number; department_id?: number },
    format: 'csv' | 'xlsx',
  ): string => `/api/v1/reports/${name}/export${query({ ...params, format })}`,

  // --------------------------------------------------------------------- proposals -------

  proposals: (params: {
    status?: readonly ProposalStatus[];
    department_id?: number;
    budget_year?: number;
    from?: string;
    to?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ total: number; items: ProposalListItem[] }> =>
    get(`/api/v1/proposals${query({ ...params, status: params.status })}`),

  proposal: (id: number): Promise<Proposal> => get(`/api/v1/proposals/${id}`),

  proposalPreview: (id: number): Promise<ConfirmPreview> => get(`/api/v1/proposals/${id}/preview`),

  createProposal: (body: {
    department_id: number;
    activity_type: string;
    activity_title: string;
    activity_description?: string | null;
    scope_note?: string | null;
    proposed_date: string;
    proposed_end_date?: string | null;
    venue?: string | null;
    remark?: string | null;
    requested_amount_cents?: number | null;
  }): Promise<{ id: number; status: ProposalStatus; version: number }> =>
    post('/api/v1/proposals', body),

  updateProposal: (
    id: number,
    body: Record<string, unknown>,
  ): Promise<{ id: number; status: ProposalStatus; version: number }> =>
    patch(`/api/v1/proposals/${id}`, body),

  setProposalAttendees: (id: number, employeeIds: number[]): Promise<AttendeesResult> =>
    put(`/api/v1/proposals/${id}/attendees`, { employee_ids: employeeIds }),

  submitProposal: (
    id: number,
    body: { justifications?: Record<string, string>; requested_amount_cents?: number },
  ): Promise<SubmitResult> => post(`/api/v1/proposals/${id}/submit`, body),

  withdrawProposal: (id: number, body: { reason?: string; expected_version?: number }) =>
    post<{ id: number; status: ProposalStatus; version: number }>(
      `/api/v1/proposals/${id}/withdraw`,
      body,
    ),

  proposalSponsorAction: (
    id: number,
    body: { action: 'endorse' | 'return' | 'reject'; note?: string; expected_version?: number },
  ) =>
    post<{ id: number; status: ProposalStatus; version: number }>(
      `/api/v1/proposals/${id}/sponsor-action`,
      body,
    ),

  takeProposalForHr: (id: number, body: { note?: string; expected_version?: number }) =>
    post<{ id: number; status: ProposalStatus; version: number }>(
      `/api/v1/proposals/${id}/hr-review`,
      body,
    ),

  returnProposal: (id: number, body: { reason: string; expected_version?: number }) =>
    post<{ id: number; status: ProposalStatus; version: number }>(
      `/api/v1/proposals/${id}/return`,
      body,
    ),

  rejectProposal: (id: number, body: { reason: string; expected_version?: number }) =>
    post<{ id: number; status: ProposalStatus; version: number }>(
      `/api/v1/proposals/${id}/reject`,
      body,
    ),

  amendProposalAttendees: (
    id: number,
    body: { add?: number[]; remove?: number[]; reason: string; expected_version?: number },
  ) =>
    put<{
      id: number;
      version: number;
      pool_cents: number;
      attendees: Calculation['attendees'];
      issues: Calculation['issues'];
    }>(`/api/v1/proposals/${id}/amend-attendees`, body),

  confirmProposal: (
    id: number,
    body: { approved_amount_cents: number; note?: string; expected_version?: number },
  ) =>
    post<{
      id: number;
      ref_no: string | null;
      status: ProposalStatus;
      version: number;
      approved_amount_cents: number;
      claim_deadline: string;
      pool_cents: number;
      holds: Array<{ employee_id: number; amount_cents: number }>;
    }>(`/api/v1/proposals/${id}/confirm`, body, { 'idempotency-key': idempotencyKey() }),

  cancelProposal: (id: number, body: { reason: string; expected_version?: number }) =>
    post<{ id: number; status: ProposalStatus; version: number; released_cents: number }>(
      `/api/v1/proposals/${id}/cancel`,
      body,
    ),

  reopenProposal: (id: number, body: { reason: string; expected_version?: number }) =>
    post<{ id: number; status: ProposalStatus; version: number; released_cents: number }>(
      `/api/v1/proposals/${id}/reopen`,
      body,
    ),

  expireProposal: (id: number, body: { reason?: string; expected_version?: number }) =>
    post<{ id: number; status: ProposalStatus; version: number; released_cents: number }>(
      `/api/v1/proposals/${id}/expire`,
      body,
    ),

  askProposalClarification: (
    id: number,
    body: { body: string; link_url?: string; expected_version?: number },
  ) =>
    post<{ clarification_id: number; status: ProposalStatus; version: number }>(
      `/api/v1/proposals/${id}/clarifications`,
      body,
    ),

  replyProposalClarification: (
    id: number,
    clarificationId: number,
    body: { body: string; link_url?: string },
  ) =>
    post<{ clarification_id: number; status: ProposalStatus; version: number }>(
      `/api/v1/proposals/${id}/clarifications/${clarificationId}/messages`,
      body,
    ),

  closeProposalClarification: (id: number, clarificationId: number) =>
    post<{ closed: boolean }>(
      `/api/v1/proposals/${id}/clarifications/${clarificationId}/close`,
      {},
    ),

  raiseProposalException: (id: number, body: { rule_id: string; justification: string }) =>
    post<{ id: number }>(`/api/v1/proposals/${id}/exceptions`, body),

  decideProposalException: (
    id: number,
    exceptionId: number,
    body: { decision: 'confirmed' | 'declined'; note?: string },
  ) => post<{ decided: boolean }>(`/api/v1/proposals/${id}/exceptions/${exceptionId}/decide`, body),

  // ------------------------------------------------------------------------ claims -------

  claims: (params: {
    status?: readonly ClaimStatus[];
    department_id?: number;
    budget_year?: number;
    limit?: number;
    offset?: number;
  }): Promise<{ total: number; items: ClaimListItem[] }> =>
    get(`/api/v1/claims${query({ ...params, status: params.status })}`),

  claim: (id: number): Promise<Claim> => get(`/api/v1/claims/${id}`),

  settlement: (id: number): Promise<Settlement> => get(`/api/v1/claims/${id}/settlement`),

  createClaim: (proposalId: number) =>
    post<{ id: number; status: ClaimStatus; version: number }>('/api/v1/claims', {
      proposal_id: proposalId,
    }),

  updateClaim: (id: number, body: Record<string, unknown>) =>
    patch<{ id: number; status: ClaimStatus; version: number }>(`/api/v1/claims/${id}`, body),

  setClaimAttendees: (id: number, employeeIds: number[]) =>
    put<{
      id: number;
      version: number;
      attendee_count: number;
      participation_pct: number | null;
      pre_vetted: number;
      not_pre_vetted: number;
    }>(`/api/v1/claims/${id}/attendees`, { employee_ids: employeeIds }),

  setClaimEvidence: (id: number, body: { drive_url: string; shared_ack: boolean }) =>
    put<{ id: number; version: number; shared_ack: boolean }>(
      `/api/v1/claims/${id}/evidence`,
      body,
    ),

  setClaimArchive: (id: number, archiveUrl: string) =>
    put<{ id: number; version: number; archive_url: string | null }>(
      `/api/v1/claims/${id}/evidence/archive`,
      { archive_url: archiveUrl },
    ),

  submitClaim: (id: number, body: { justifications?: Record<string, string> }) =>
    post<{
      id: number;
      ref_no: string | null;
      status: ClaimStatus;
      version: number;
      sponsor_skipped: boolean;
      exceptions_raised: Array<{ rule_id: string }>;
      warnings: Calculation['issues'];
    }>(`/api/v1/claims/${id}/submit`, body),

  claimSponsorAction: (
    id: number,
    body: { action: 'endorse' | 'return' | 'reject'; note?: string; expected_version?: number },
  ) =>
    post<{ id: number; status: ClaimStatus; version: number }>(
      `/api/v1/claims/${id}/sponsor-action`,
      body,
    ),

  verifyClaim: (
    id: number,
    body: { action: 'verify' | 'return' | 'reject'; note?: string; expected_version?: number },
  ) =>
    post<{ id: number; status: ClaimStatus; version: number }>(
      `/api/v1/claims/${id}/hr-verify`,
      body,
    ),

  /** The Director's decision. Settles the ledger on approve, so it always carries a key. */
  approveClaim: (
    id: number,
    body: { action: 'approve' | 'return' | 'reject'; note?: string; expected_version?: number },
    key: string,
  ) =>
    post<{
      id: number;
      ref_no: string | null;
      status: ClaimStatus;
      version: number;
      settled: boolean;
      reimbursable_cents: number | null;
      excess_cents: number | null;
      released_cents: number | null;
      replayed?: boolean;
    }>(`/api/v1/claims/${id}/hr-approve`, body, { 'idempotency-key': key }),

  seniorTopup: (
    id: number,
    body: { amount_cents: number; justification: string; expected_version?: number },
  ) =>
    post<{ id: number; version: number; senior_topup_cents: number | null; rule_id: string }>(
      `/api/v1/claims/${id}/senior-topup`,
      body,
    ),

  markClaimPaid: (
    id: number,
    body: {
      paid_at?: string;
      payment_reference: string;
      note?: string;
      expected_version?: number;
    },
    key: string,
  ) =>
    post<{
      id: number;
      status: ClaimStatus;
      version: number;
      paid_at: string | null;
      payment_reference: string | null;
      replayed?: boolean;
    }>(`/api/v1/claims/${id}/mark-paid`, body, { 'idempotency-key': key }),

  askClaimClarification: (
    id: number,
    body: { body: string; link_url?: string; expected_version?: number },
  ) =>
    post<{ clarification_id: number; status: ClaimStatus; version: number }>(
      `/api/v1/claims/${id}/clarifications`,
      body,
    ),

  replyClaimClarification: (
    id: number,
    clarificationId: number,
    body: { body: string; link_url?: string },
  ) =>
    post<{ clarification_id: number; status: ClaimStatus; version: number }>(
      `/api/v1/claims/${id}/clarifications/${clarificationId}/messages`,
      body,
    ),

  raiseClaimException: (id: number, body: { rule_id: string; justification: string }) =>
    post<{ id: number }>(`/api/v1/claims/${id}/exceptions`, body),

  decideClaimException: (
    id: number,
    exceptionId: number,
    body: { decision: 'confirmed' | 'declined'; note?: string },
  ) => post<{ decided: boolean }>(`/api/v1/claims/${id}/exceptions/${exceptionId}/decide`, body),

  // ------------------------------------------------------------------------- admin -------

  adminUsers: (): Promise<{ users: AdminUser[]; pending: PendingUser[] }> =>
    get('/api/v1/admin/users'),

  addUser: (body: { email: string; role: string; note?: string }) =>
    post<{ email: string; intended_role: string; next_step: string }>('/api/v1/admin/users', body),

  setUserRole: (id: number, role: string) =>
    put<Record<string, unknown>>(`/api/v1/admin/users/${id}/role`, { role }),

  deactivateUser: (id: number, reason: string) =>
    post<Record<string, unknown>>(`/api/v1/admin/users/${id}/deactivate`, { reason }),

  reactivateUser: (id: number) =>
    post<Record<string, unknown>>(`/api/v1/admin/users/${id}/reactivate`, {}),

  revokeUser: (id: number, reason: string) =>
    post<Record<string, unknown>>(`/api/v1/admin/users/${id}/revoke`, { reason }),

  auditTrail: (limit = 100) =>
    get<{ events: Array<Record<string, unknown>> }>(`/api/v1/admin/audit${query({ limit })}`),

  // ------------------------------------------------------------------ notifications -----

  notifications: (limit = 30): Promise<NotificationList> =>
    get(`/api/v1/me/notifications${query({ limit })}`),

  readNotification: (id: number) =>
    post<{ id: number; read: boolean }>(`/api/v1/me/notifications/${id}/read`, {}),

  readAllNotifications: () => post<{ read: number }>('/api/v1/me/notifications/read-all', {}),

  outbox: (params: { status?: OutboxStatus; limit?: number } = {}): Promise<OutboxList> =>
    get(`/api/v1/admin/outbox${query(params)}`),

  jobs: (
    params: { job?: string; limit?: number; include_noops?: boolean } = {},
  ): Promise<JobList> => get(`/api/v1/admin/jobs${query(params)}`),

  runJob: (job: string): Promise<JobRunResult> => post(`/api/v1/admin/jobs/${job}/run`, {}),

  // ---------------------------------------------------------------------- feedback ------

  raiseFeedback: (body: {
    category: FeedbackCategory;
    body: string;
    context?: FeedbackContext;
  }): Promise<{ id: number; status: FeedbackStatus; created_at: string }> =>
    post('/api/v1/feedback', body),

  myFeedback: (): Promise<MyFeedbackList> => get('/api/v1/feedback/mine'),

  markFeedbackSeen: (id: number) =>
    post<{ id: number; seen: boolean }>(`/api/v1/feedback/${id}/seen`, {}),

  managedFeedback: (
    params: { status?: FeedbackStatus; limit?: number } = {},
  ): Promise<ManagedFeedbackList> => get(`/api/v1/admin/feedback${query(params)}`),

  pendingDigest: (): Promise<{ pending: Array<{ id: number }>; count: number }> =>
    get('/api/v1/admin/feedback/pending-digest'),

  updateFeedback: (id: number, body: { status?: FeedbackStatus; admin_remark?: string }) =>
    patch<{ id: number; status: FeedbackStatus; admin_remark: string | null }>(
      `/api/v1/admin/feedback/${id}`,
      body,
    ),
};
